/**
 * Lodash to ES-Toolkit Migration
 *
 * This file has been migrated from lodash to es-toolkit for better performance and smaller bundle size.
 * All lodash functions have been replaced with either native JavaScript or es-toolkit equivalents.
 *
 * Usage:
 * import { get, isEmpty, compact } from './lodash-optimized'
 */

// ========================================
// NATIVE REPLACEMENTS (0 KB bundle impact)
// ========================================

// _.isNil() - Check if value is null or undefined
export const isNil = (value: any): boolean => value == null

// _.compact() - Remove falsy values from array
export const compact = <T>(arr: T[]): T[] => arr.filter(Boolean) as T[]

// _.capitalize() - Capitalize first letter
export const capitalize = (str: string | null | undefined = ''): string => {
    if (!str) return ''
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
}

// _.identity() - Returns its argument
export const identity = <T>(value: T): T => value

// _.last() - Get last element of array
export const last = <T>(arr: T[]): T | undefined => arr[arr.length - 1]

// _.startCase() - Convert to start case
export const startCase = (str: string | null | undefined = ''): string => {
    if (!str) return ''
    return str
        .replace(/([A-Z])/g, ' $1')
        .replace(/[_-]/g, ' ')
        .trim()
        .split(/\s+/)
        .map(word => capitalize(word))
        .join(' ')
}

// _.upperCase() - Convert to upper case
export const upperCase = (str: string = ''): string => str.toUpperCase()

// _.toUpper() - Alias for upperCase
export const toUpper = upperCase

// _.first() - Get first element of array
export const first = <T>(arr: T[]): T | undefined => arr[0]

// _.range() - Create array of numbers
export const range = (start: number, end?: number, step: number = 1): number[] => {
    if (end === undefined) {
        end = start
        start = 0
    }

    const length = Math.max(Math.ceil((end - start) / step), 0)
    const result = Array(length)

    for (let i = 0; i < length; i++) {
        result[i] = start + (i * step)
    }

    return result
}

// _.values() - Get object values
export const values = <T>(obj: Record<string, T> | null | undefined): T[] => {
    if (!obj) return []
    return Object.values(obj)
}

// _.keys() - Get object keys
export const keys = (obj: Record<string, any> | null | undefined): string[] => {
    if (!obj) return []
    return Object.keys(obj)
}

// _.has() - Check if object has property (supports nested paths)
export const has = (obj: any, path: string | string[]): boolean => {
    if (!obj) return false
    const keys = Array.isArray(path) ? path : path.split('.')
    let current = obj
    for (const key of keys) {
        if (current == null || !(key in current)) {
            return false
        }
        current = current[key]
    }
    return true
}

// _.assign() - Assign properties (use Object.assign or spread instead)
export const assign = Object.assign

// _.includes() - Check if array/string includes value
export const includes = <T>(collection: T[] | string | null | undefined, value: T): boolean => {
    if (!collection) return false
    if (Array.isArray(collection)) {
        return collection.includes(value)
    }
    if (typeof collection === 'string' && typeof value === 'string') {
        return collection.includes(value)
    }
    return false
}

// _.extend() - Alias for assign
export const extend = assign

// _.concat() - Concatenate arrays/values
export const concat = <T>(...args: (T | T[])[]): T[] => {
    const result: T[] = []
    for (const arg of args) {
        if (Array.isArray(arg)) {
            result.push(...arg)
        } else if (arg !== undefined && arg !== null) {
            result.push(arg as T)
        }
    }
    return result
}

// _.head() - Alias for first
export const head = first

// _.parseInt() - Parse string to integer
export const parseInt = (str: string, radix: number = 10): number => {
    const result = Number.parseInt(str, radix)
    return isNaN(result) ? 0 : result
}

// _.toString() - Convert to string
export const toString = (value: any): string => {
    if (value == null) return ''
    if (typeof value === 'string') return value
    return String(value)
}

// _.toLower() - Convert to lowercase
export const toLower = (str: string = ''): string => str.toLowerCase()

// _.toNumber() - Convert to number
export const toNumber = (value: any): number => {
    if (typeof value === 'number') return value
    const num = Number(value)
    return isNaN(num) ? 0 : num
}

// _.lowerCase() - Convert to lower case with spaces
export const lowerCase = (str: string = ''): string => {
    return str
        .replace(/([A-Z])/g, ' $1')
        .replace(/[_-]/g, ' ')
        .trim()
        .toLowerCase()
}

// _.words() - Split string into words
export const words = (str: string = ''): string[] => {
    return str.match(/[A-Z]?[a-z]+|[0-9]+|[A-Z]+(?![a-z])/g) || []
}

// _.truncate() - Truncate string
export const truncate = (str: string = '', options: { length?: number, omission?: string } = {}): string => {
    const {length = 30, omission = '...'} = options
    if (str.length <= length) return str
    return str.slice(0, length - omission.length) + omission
}

// _.get() - Safe property access (use optional chaining instead when possible)
const get = (obj: any, path: string | string[], defaultValue?: any): any => {
    if (!path) return obj
    const keys = Array.isArray(path) ? path : path.split('.')
    let result = obj
    for (const key of keys) {
        if (key.includes('[')) {
            const parts = key.split(/[\[\]]/).filter(Boolean)
            for (const part of parts) {
                result = result?.[part]
                if (result === undefined) return defaultValue
            }
            continue
        }
        result = result?.[key]
        if (result === undefined) return defaultValue
    }
    return result ?? defaultValue
}

// ========================================
// ES-TOOLKIT IMPORTS
// ========================================

// Object utilities
import {isEmpty} from 'es-toolkit/compat'
import {isEqual} from 'es-toolkit'
import {cloneDeep} from 'es-toolkit/compat'
import {pick} from 'es-toolkit'
import {omit} from 'es-toolkit'
import {pickBy} from 'es-toolkit'
import {omitBy} from 'es-toolkit'
import {merge} from 'es-toolkit'
import {set} from 'es-toolkit/compat'
import {mapValues} from 'es-toolkit'
import {keyBy} from 'es-toolkit'

// Function utilities
import {debounce} from 'es-toolkit'
import {throttle} from 'es-toolkit'

// Array utilities
import {sortBy} from 'es-toolkit'
import {groupBy} from 'es-toolkit'
import {uniq} from 'es-toolkit'
import {uniqBy} from 'es-toolkit'
import {find} from 'es-toolkit/compat'
import {filter} from 'es-toolkit/compat'
import {map} from 'es-toolkit/compat'
import {reduce} from 'es-toolkit/compat'
import {difference} from 'es-toolkit'
import {intersection} from 'es-toolkit'
import {pull as pullEs} from 'es-toolkit'
import {orderBy as orderByEs} from 'es-toolkit'

// String utilities
import {camelCase} from 'es-toolkit'
import {snakeCase} from 'es-toolkit'
import {kebabCase} from 'es-toolkit'

// Re-export for named imports
export {
    isEmpty, isEqual, cloneDeep, pick, omit, pickBy, omitBy, merge, set,
    mapValues, keyBy, debounce, throttle, sortBy, groupBy, uniq, uniqBy,
    find, filter, map, reduce, camelCase, snakeCase, kebabCase, difference,
    intersection
}

// Additional array utilities
export const pull = pullEs
export const orderBy = orderByEs

// _.findIndex() - Find index by predicate
export const findIndex = <T>(arr: T[], predicate: ((value: T, index: number, array: T[]) => boolean) | T): number => {
    if (!arr) return -1

    // If predicate is a function, use it
    if (typeof predicate === 'function') {
        const predicateFn = predicate as (value: T, index: number, array: T[]) => boolean
        for (let i = 0; i < arr.length; i++) {
            const item = arr[i]
            if (item !== undefined && predicateFn(item, i, arr)) {
                return i
            }
        }
        return -1
    }

    // If predicate is a value, find its index
    return arr.indexOf(predicate as T)
}

// _.findKey() - Find key by predicate
export const findKey = (obj: Record<string, any>, predicate: (value: any, key: string) => boolean): string | undefined => {
    for (const key in obj) {
        if (obj.hasOwnProperty(key) && predicate(obj[key], key)) {
            return key
        }
    }
    return undefined
}

// _.intersectionWith() - Intersection with comparator
export const intersectionWith = <T>(arr1: T[], arr2: T[], comparator: (a: T, b: T) => boolean): T[] => {
    if (!arr1 || !arr2) return []
    return arr1.filter(item1 => arr2.some(item2 => comparator(item1, item2)))
}

// _.uniqWith() - Unique with comparator
export const uniqWith = <T>(arr: T[], comparator: (a: T, b: T) => boolean): T[] => {
    if (!arr) return []
    const result: T[] = []
    for (const item of arr) {
        if (!result.some(existing => comparator(existing, item))) {
            result.push(item)
        }
    }
    return result
}

// _.remove() - Remove elements from array (mutates)
export const remove = <T>(arr: T[], predicate: (value: T, index: number, array: T[]) => boolean): T[] => {
    const removed: T[] = []
    let i = arr.length
    while (i--) {
        const item = arr[i]
        if (item !== undefined && predicate(item, i, arr)) {
            removed.push(...arr.splice(i, 1))
        }
    }
    return removed.reverse()
}

// _.unset() - Delete nested property
export const unset = (obj: any, path: string | string[]): boolean => {
    if (!obj) return true
    const keys = Array.isArray(path) ? path : path.split('.')
    const lastKey = keys.pop()
    if (!lastKey) return true

    let current = obj
    for (const key of keys) {
        current = current[key]
        if (!current) return true
    }

    delete current[lastKey]
    return true
}

// _.mapKeys() - Map object keys
export const mapKeys = (obj: Record<string, any>, fn: (value: any, key: string) => string): Record<string, any> => {
    const result: Record<string, any> = {}
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            const newKey = fn(obj[key], key)
            result[newKey] = obj[key]
        }
    }
    return result
}

// _.size() - Get collection size
export const size = (collection: any): number => {
    if (collection == null) return 0
    if (Array.isArray(collection) || typeof collection === 'string') {
        return collection.length
    }
    if (typeof collection === 'object') {
        return Object.keys(collection).length
    }
    return 0
}

// _.max() - Get maximum value
export const max = (arr: number[]): number | undefined => {
    if (!arr || !arr.length) return undefined
    return Math.max(...arr)
}

// _.join() - Join array elements
export const join = <T>(arr: T[], separator: string = ','): string => {
    if (!arr) return ''
    return arr.join(separator)
}

// _.split() - Split string
export const split = (str: string, separator: string | RegExp, limit?: number): string[] => {
    if (!str) return []
    return str.split(separator, limit)
}

// Type checks - Native implementations
export const isArray = Array.isArray
export const isObject = (value: any): boolean =>
    value !== null && typeof value === 'object' && !Array.isArray(value)
export const isString = (value: any): value is string => typeof value === 'string'
export const isNumber = (value: any): value is number => typeof value === 'number'
export const isInteger = (value: any): value is number => Number.isInteger(value)
export const isFunction = (value: any): value is Function => typeof value === 'function'
export const isNotNil = (value: any): boolean => value != null
export const isNull = (value: any): value is null => value === null
export const isUndefined = (value: any): value is undefined => value === undefined
export const isFinite = (value: any): value is number => Number.isFinite(value)
export const isNaN = (value: any): boolean => Number.isNaN(value)
export const isPlainObject = (value: any): boolean => {
    if (!isObject(value)) return false
    const proto = Object.getPrototypeOf(value)
    return proto === null || proto === Object.prototype
}

// ========================================
// CHAIN HELPER (_.chain replacement)
// ========================================

type ChainWrapper<T> = {
    value(): T
    map<U>(fn: (item: any) => U): ChainWrapper<U[]>
    filter(fn: (item: any) => boolean): ChainWrapper<T>
    find(fn: (item: any) => boolean): ChainWrapper<any>
    compact(): ChainWrapper<T>
    uniq(): ChainWrapper<T>
    sort(fn?: (a: any, b: any) => number): ChainWrapper<T>
    sortBy(fn: (item: any) => any): ChainWrapper<T>
    groupBy(fn: (item: any) => string): ChainWrapper<Record<string, any[]>>
    take(n: number): ChainWrapper<T>
    first(): ChainWrapper<any>
    last(): ChainWrapper<any>
    get(path: string, defaultValue?: any): ChainWrapper<any>
    values(): ChainWrapper<any[]>
    keys(): ChainWrapper<string[]>
    split(separator: string | RegExp, limit?: number): ChainWrapper<string[]>
    join(separator?: string): ChainWrapper<string>
    reduce<U>(fn: (acc: U, item: any, index?: number, array?: any[]) => U, initial?: U): ChainWrapper<U>
}

export function chain<T>(value: T): ChainWrapper<T> {
    let current = value

    const wrapper: ChainWrapper<T> = {
        value: () => current,

        map: (fn) => {
            current = (Array.isArray(current) ? current : [current]).map(fn) as any
            return wrapper as any
        },

        filter: (fn) => {
            if (Array.isArray(current)) {
                current = current.filter(fn) as any
            }
            return wrapper
        },

        find: (fn) => {
            if (Array.isArray(current)) {
                current = current.find(fn)
            }
            return wrapper
        },

        compact: () => {
            if (Array.isArray(current)) {
                current = compact(current as any[]) as any
            }
            return wrapper
        },

        uniq: () => {
            if (Array.isArray(current)) {
                current = [...new Set(current as any[])] as any
            }
            return wrapper
        },

        sort: (fn?) => {
            if (Array.isArray(current)) {
                current = [...(current as any[])].sort(fn) as any
            }
            return wrapper
        },

        sortBy: (fn) => {
            if (Array.isArray(current)) {
                current = [...(current as any[])].sort((a, b) => {
                    const aVal = fn(a)
                    const bVal = fn(b)
                    if (aVal < bVal) return -1
                    if (aVal > bVal) return 1
                    return 0
                }) as any
            }
            return wrapper
        },

        groupBy: (fn) => {
            if (Array.isArray(current)) {
                const grouped: Record<string, any[]> = {}
                for (const item of current as any[]) {
                    const key = fn(item)
                    if (!grouped[key]) grouped[key] = []
                    grouped[key].push(item)
                }
                current = grouped as any
            }
            return wrapper as any
        },

        take: (n) => {
            if (Array.isArray(current)) {
                current = current.slice(0, n) as any
            }
            return wrapper
        },

        first: () => {
            if (Array.isArray(current)) {
                current = current[0] as any
            }
            return wrapper
        },

        last: () => {
            if (Array.isArray(current)) {
                current = last(current) as any
            }
            return wrapper
        },

        get: (path, defaultValue?) => {
            current = get(current, path, defaultValue)
            return wrapper
        },

        values: () => {
            if (current && typeof current === 'object' && !Array.isArray(current)) {
                current = Object.values(current) as any
            } else if (!current) {
                current = [] as any
            }
            return wrapper as any
        },

        split: (separator, limit?) => {
            if (typeof current === 'string') {
                current = current.split(separator, limit) as any
            } else if (current == null) {
                current = [] as any
            }
            return wrapper as any
        },

        join: (separator = ',') => {
            if (Array.isArray(current)) {
                current = current.join(separator) as any
            } else if (current == null) {
                current = '' as any
            }
            return wrapper as any
        },

        reduce: (fn, initial?) => {
            if (Array.isArray(current)) {
                current = initial !== undefined
                    ? current.reduce(fn, initial)
                    : current.reduce(fn)
            }
            return wrapper as any
        },

        keys: () => {
            if (current && typeof current === 'object' && !Array.isArray(current)) {
                current = Object.keys(current) as any
            } else if (!current) {
                current = [] as any
            }
            return wrapper as any
        }
    }

    return wrapper
}

// ========================================
// DEFAULT EXPORT (for gradual migration)
// ========================================

const _ = {
    // Native implementations
    isNil,
    compact,
    capitalize,
    identity,
    last,
    first,
    head,
    range,
    startCase,
    upperCase,
    toUpper,
    toLower,
    lowerCase,
    get,  // Native implementation for compatibility
    values,
    keys,
    has,
    assign,
    includes,
    extend,
    concat,
    parseInt,
    toString,
    toNumber,
    words,
    truncate,
    join,
    split,
    size,
    max,

    // ES-toolkit functions
    set,
    isEmpty,
    isEqual,
    cloneDeep,
    pick,
    omit,
    pickBy,
    omitBy,
    merge,
    debounce,
    throttle,
    sortBy,
    groupBy,
    uniq,
    uniqBy,
    uniqWith,
    find,
    findIndex,
    findKey,
    filter,
    map,
    mapKeys,
    reduce,
    mapValues,
    keyBy,
    camelCase,
    snakeCase,
    kebabCase,
    difference,
    intersection,
    intersectionWith,
    pull,
    remove,
    unset,
    orderBy,

    // Type checks
    isArray,
    isObject,
    isPlainObject,
    isString,
    isNumber,
    isInteger,
    isFinite,
    isNaN,
    isFunction,
    isNull,
    isUndefined,

    // Chain
    chain
}

export default _
